import * as React from 'react'
import { cn } from '@/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'

/**
 * Typography Components
 *
 * Provides consistent heading and text styles across the application.
 * Based on the actual usage patterns found in the codebase.
 */

const headingVariants = cva(
  'tracking-tight text-gray-900 dark:text-foreground font-space-grotesk',
  {
    variants: {
      variant: {
        // Page-level headings (h1)
        'page-title': 'text-2xl sm:text-3xl font-bold',
        // Section headings (h2)
        'section-title': 'text-md sm:text-lg font-bold',
        // Subsection headings (h3)
        'subsection-title': 'text-lg font-semibold',
        'card-title': 'text-sm font-semibold mb-1',
        // Small headings (h4)
        'label-heading': 'text-sm font-medium',
      },
    },
    defaultVariants: {
      variant: 'subsection-title',
    },
  },
)

export interface HeadingProps
  extends React.HTMLAttributes<HTMLHeadingElement>,
    VariantProps<typeof headingVariants> {
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
}

const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ className, variant, as, ...props }, ref) => {
    // Default to semantic HTML based on variant if `as` prop not provided
    const defaultTag = {
      'page-title': 'h1',
      'section-title': 'h2',
      'subsection-title': 'h3',
      'card-title': 'h3',
      'label-heading': 'h4',
    }[variant || 'subsection-title'] as 'h1' | 'h2' | 'h3' | 'h4'

    const Comp = as || defaultTag

    return (
      <Comp
        ref={ref}
        className={cn(headingVariants({ variant, className }))}
        {...props}
      />
    )
  },
)
Heading.displayName = 'Heading'

const textVariants = cva('', {
  variants: {
    variant: {
      // Body text
      body: 'text-base text-gray-700 dark:text-foreground',
      'body-sm': 'text-sm text-gray-700 dark:text-foreground',
      'body-lg': 'text-lg text-gray-700 dark:text-foreground',
      // Muted/secondary text
      muted: 'text-sm text-gray-500 dark:text-muted-foreground',
      'muted-xs': 'text-xs text-gray-500 dark:text-muted-foreground',
      caption: 'text-xs text-muted-foreground',
      // Special variants
      subtle: 'text-sm text-gray-600 dark:text-muted-foreground',
      lead: 'text-xl text-gray-600 dark:text-muted-foreground',
      // Metric display (for numbers/stats)
      metric:
        'text-4xl font-extrabold tabular-nums text-gray-900 dark:text-foreground',
    },
    weight: {
      normal: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
  },
  defaultVariants: {
    variant: 'body',
  },
})

export interface TextProps
  extends React.HTMLAttributes<HTMLParagraphElement>,
    VariantProps<typeof textVariants> {
  as?: 'p' | 'span' | 'div'
}

const Text = React.forwardRef<
  HTMLParagraphElement | HTMLSpanElement | HTMLDivElement,
  TextProps
>(({ className, variant, weight, as: Comp = 'p', ...props }, ref) => {
  return (
    <Comp
      ref={ref as any}
      className={cn(textVariants({ variant, weight, className }))}
      {...props}
    />
  )
})
Text.displayName = 'Text'

const codeVariants = cva('font-mono rounded', {
  variants: {
    variant: {
      inline: 'bg-muted text-xs px-1.5 py-0.5',
      block: 'block bg-muted p-4 text-sm overflow-x-auto',
    },
  },
  defaultVariants: {
    variant: 'inline',
  },
})

export interface CodeProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof codeVariants> {}

const Code = React.forwardRef<HTMLElement, CodeProps>(
  ({ className, variant, ...props }, ref) => {
    return (
      <code
        ref={ref}
        className={cn(codeVariants({ variant, className }))}
        {...props}
      />
    )
  },
)
Code.displayName = 'Code'

/**
 * PageTitle - Composite component for page-level headers
 * Combines a main title with optional subtitle
 */
export interface PageTitleProps extends Omit<HeadingProps, 'variant' | 'as'> {
  subtitle?: string
  subtitleClassName?: string
}

const PageTitle = React.forwardRef<HTMLDivElement, PageTitleProps>(
  ({ subtitle, children, className, subtitleClassName, ...props }, ref) => {
    return (
      <div ref={ref} className="flex flex-col gap-1 sm:gap-2">
        <Heading variant="page-title" className={className} as="h1" {...props}>
          {children}
        </Heading>
        {subtitle && (
          <Text variant="muted" className={subtitleClassName}>
            {subtitle}
          </Text>
        )}
      </div>
    )
  },
)
PageTitle.displayName = 'PageTitle'

/**
 * SectionTitle - Composite component for section headers
 * Combines a section title with optional description
 */
export interface SectionTitleProps
  extends Omit<HeadingProps, 'variant' | 'as'> {
  description?: string
  descriptionClassName?: string
}

const SectionTitle = React.forwardRef<HTMLDivElement, SectionTitleProps>(
  (
    { description, children, className, descriptionClassName, ...props },
    ref,
  ) => {
    return (
      <div ref={ref} className="flex flex-col gap-1">
        <Heading
          variant="section-title"
          className={className}
          as="h2"
          {...props}
        >
          {children}
        </Heading>
        {description && (
          <Text variant="muted" className={descriptionClassName}>
            {description}
          </Text>
        )}
      </div>
    )
  },
)
SectionTitle.displayName = 'SectionTitle'

export { Heading, Text, Code, PageTitle, SectionTitle }
